"use client";

import React from "react";
import { MapPin, Phone, Mail, Clock, Send, Loader, Check, AlertCircle } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage, FormDescription } from "@/components/ui/form";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
const SHARED_DATA = {
  address: "Ouagadougou, Pissy, en face du Lycée Vénégré au 2e étage, Secteur 27 lot 31 parcelle 20 section OA",
  phone: "+226 07 00 00 00",
  email: "contact@freelancecomandevent.com",
  hours: "Lundi au Vendredi, 09h00 - 18h00"
};
function ContactForm() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="flex flex-col items-center justify-center h-full min-h-[400px] p-8 text-center bg-card/50 backdrop-blur-xl rounded-lg border border-border animate-in fade-in zoom-in duration-500">
        <div className="w-16 h-16 bg-primary/10 text-primary rounded-full flex items-center justify-center mb-6 shadow-sm">
          <Check className="w-8 h-8" />
        </div>
        <h3 className="text-2xl font-serif font-bold text-foreground mb-2">Message Envoyé</h3>
        <p className="text-muted-foreground mb-8 max-w-md">
          {successMessage}
        </p>
        <Button onClick={resetForm} variant="outline" className="rounded-lg border-primary/20 hover:bg-primary/5">
          Envoyer un autre message
        </Button>
      </div>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <FormField control={control} name="name" rules={{
        required: "Le nom complet est requis"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel>Nom complet</FormLabel>
              <FormControl>
                <Input {...field} type="text" placeholder="" className="h-12 bg-background/50 border-border/50 rounded-lg focus-visible:ring-primary/20" />
              </FormControl>
              <FormDescription className="text-xs">
                Indiquez votre nom tel qu'il apparaît sur vos documents officiels.
              </FormDescription>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="email" rules={{
        required: "L'adresse e-mail est requise",
        pattern: {
          value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
          message: "Adresse e-mail invalide"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel>Adresse e‑mail</FormLabel>
              <FormControl>
                <Input {...field} type="email" placeholder="" className="h-12 bg-background/50 border-border/50 rounded-lg focus-visible:ring-primary/20" />
              </FormControl>
              <FormMessage />
            </FormItem>} />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <FormField control={control} name="phone" rules={{
        pattern: {
          value: /^[+]?[(]?[0-9]{3}[)]?[-\s.]?[0-9]{3}[-\s.]?[0-9]{4,6}$/,
          message: "Numéro de téléphone invalide"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel>Numéro de téléphone</FormLabel>
              <FormControl>
                <Input {...field} type="tel" placeholder="" className="h-12 bg-background/50 border-border/50 rounded-lg focus-visible:ring-primary/20" />
              </FormControl>
              <FormDescription className="text-xs">
                Facultatif – nous permet de vous répondre rapidement.
              </FormDescription>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="organization" render={({
        field
      }) => <FormItem>
              <FormLabel>Organisation / Entreprise</FormLabel>
              <FormControl>
                <Input {...field} type="text" placeholder="" className="h-12 bg-background/50 border-border/50 rounded-lg focus-visible:ring-primary/20" />
              </FormControl>
              <FormMessage />
            </FormItem>} />
      </div>

      <FormField control={control} name="preferred_contact" render={({
      field
    }) => <FormItem>
            <FormLabel>Moyen de contact préféré</FormLabel>
            <Select onValueChange={field.onChange} defaultValue={field.value}>
              <FormControl>
                <SelectTrigger className="h-12 bg-background/50 border-border/50 rounded-lg focus:ring-primary/20">
                  <SelectValue placeholder="Sélectionnez une option" />
                </SelectTrigger>
              </FormControl>
              <SelectContent>
                <SelectItem value="email">E-mail</SelectItem>
                <SelectItem value="phone">Téléphone</SelectItem>
                <SelectItem value="none">Aucun</SelectItem>
              </SelectContent>
            </Select>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="objectives" rules={{
      required: "Veuillez décrire vos objectifs"
    }} render={({
      field
    }) => <FormItem>
            <FormLabel>Vos objectifs</FormLabel>
            <FormControl>
              <Textarea {...field} placeholder="" className="min-h-[120px] bg-background/50 border-border/50 rounded-lg focus-visible:ring-primary/20" />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      {submitError && <div className="p-4 bg-destructive/10 border border-destructive/20 rounded-lg animate-in fade-in slide-in-from-top-2">
          <p className="text-destructive text-sm font-medium flex items-center gap-2">
            <AlertCircle className="size-4" /> {submitError}
          </p>
        </div>}

      <Button type="submit" disabled={isSubmitting} className="w-full h-12 text-base font-bold transition-all duration-300 hover:shadow-lg hover:shadow-primary/20 rounded-lg">
        {isSubmitting ? <>
            <Loader className="animate-spin mr-2 size-5" />
            Envoi en cours...
          </> : <>
            Envoyer ma demande
            <Send className="ml-2 size-4" />
          </>}
      </Button>
    </Form>;
}
export default function DiscutonsDeVosObjectifs() {
  return <section id="discutons-de-vos-objectifs" className="relative py-24 lg:py-32 overflow-hidden bg-background">
      <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none">
        <div className="absolute top-[-10%] right-[-5%] w-[500px] h-[500px] bg-primary/5 rounded-full blur-3xl opacity-50" />
        <div className="absolute bottom-[-10%] left-[-5%] w-[500px] h-[500px] bg-secondary/5 rounded-full blur-3xl opacity-50" />
      </div>

      <div className="container relative mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-24 items-start">
          
          <div className="flex flex-col gap-10 order-2 lg:order-1">
            <div className="space-y-6">
              <h2 className="text-4xl md:text-5xl font-serif font-bold text-foreground tracking-tight">
                Parlons de votre <span className="text-primary">Projet</span>
              </h2>
              <p className="text-lg text-muted-foreground leading-relaxed max-w-lg">
                Notre équipe est prête à transformer vos ambitions en stratégies concrètes. 
                Contactez-nous pour une consultation personnalisée et un accompagnement sur mesure.
              </p>
            </div>

            <div className="grid gap-6">
              <div className="flex items-start gap-4 p-6 rounded-lg bg-card border border-border/50 shadow-sm hover:shadow-md transition-all duration-300">
                <div className="flex-shrink-0 w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center text-primary">
                  <MapPin className="size-6" />
                </div>
                <div>
                  <h3 className="font-bold text-foreground mb-1">Notre Bureau</h3>
                  <p className="text-muted-foreground text-sm leading-relaxed">{SHARED_DATA.address}</p>
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                <div className="flex items-start gap-4 p-6 rounded-lg bg-card border border-border/50 shadow-sm hover:shadow-md transition-all duration-300">
                  <div className="flex-shrink-0 w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center text-primary">
                    <Phone className="size-6" />
                  </div>
                  <div>
                    <h3 className="font-bold text-foreground mb-1">Téléphone</h3>
                    <Link to={`tel:${SHARED_DATA.phone}`} className="text-muted-foreground hover:text-primary transition-colors text-sm">{SHARED_DATA.phone}</Link>
                  </div>
                </div>

                <div className="flex items-start gap-4 p-6 rounded-lg bg-card border border-border/50 shadow-sm hover:shadow-md transition-all duration-300">
                  <div className="flex-shrink-0 w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center text-primary">
                    <Mail className="size-6" />
                  </div>
                  <div>
                    <h3 className="font-bold text-foreground mb-1">E-mail</h3>
                    <Link to={`mailto:${SHARED_DATA.email}`} className="text-muted-foreground hover:text-primary transition-colors break-all text-sm">contact@freelancecomevent.com</Link>
                  </div>
                </div>
              </div>

              <div className="flex items-start gap-4 p-6 rounded-lg bg-card border border-border/50 shadow-sm hover:shadow-md transition-all duration-300">
                <div className="flex-shrink-0 w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center text-primary">
                  <Clock className="size-6" />
                </div>
                <div>
                  <h3 className="font-bold text-foreground mb-1">Horaires d'ouverture</h3>
                  <p className="text-muted-foreground text-sm">{SHARED_DATA.hours}</p>
                </div>
              </div>
            </div>

            <div className="relative w-full h-72 rounded-lg overflow-hidden border border-border shadow-lg group">
              <div className="absolute inset-0 bg-muted animate-pulse" />
              <img src="/img/agency_hq_location_map.png" alt="Carte de localisation de l'agence à Ouagadougou" className="w-full h-full object-cover opacity-90 group-hover:opacity-100 group-hover:scale-105 transition-all duration-700" />
              <div className="absolute inset-0 bg-gradient-to-t from-background/40 to-transparent pointer-events-none" />
              
              <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 flex flex-col items-center gap-2">
                <div className="relative">
                  <div className="w-6 h-6 bg-primary rounded-full animate-ping absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 opacity-40" />
                  <div className="relative z-10 w-12 h-12 bg-primary text-primary-foreground rounded-full flex items-center justify-center shadow-2xl border-2 border-white dark:border-secondary">
                    <MapPin className="size-6" />
                  </div>
                </div>
                <div className="bg-background/95 backdrop-blur-md px-4 py-2 rounded-md shadow-xl border border-border/50">
                  <span className="text-xs font-bold text-foreground uppercase tracking-wider">Freelance Com & Event</span>
                </div>
              </div>
            </div>
          </div>

          <div className="order-1 lg:order-2">
            <div className="bg-card/40 backdrop-blur-xl border border-white/20 rounded-lg p-6 sm:p-10 lg:p-12 shadow-xl relative overflow-hidden">
              <div className="absolute top-0 left-0 w-full h-1.5 bg-gradient-to-r from-primary via-primary/80 to-primary" />
              
              <div className="mb-10">
                <h3 className="text-3xl font-serif font-bold text-foreground mb-3">Discutons de vos Objectifs</h3>
                <p className="text-muted-foreground text-sm max-w-sm">
                  Remplissez le formulaire ci-dessous et notre équipe stratégique vous recontactera sous 24h ouvrées.
                </p>
              </div>

              <FormProvider formId="objectives_form_contact" sectionName="contact-section" defaultValues={{
              name: "",
              email: "",
              phone: "",
              organization: "",
              objectives: "",
              preferred_contact: "email"
            }} validationRules={{
              name: {
                required: true
              },
              email: {
                required: true,
                pattern: "email"
              },
              phone: {
                pattern: "phone"
              },
              objectives: {
                required: true
              }
            }} submitText="Envoyer ma demande" successMessage="Merci ! Votre message a bien été reçu. Nous vous recontacterons très prochainement." errorMessage="Une erreur est survenue lors de l'envoi. Veuillez réessayer.">
                <ContactForm />
              </FormProvider>
            </div>
          </div>

        </div>
      </div>
    </section>;
}